/**
 * Client-side funnel tracking.
 *
 * Posts to POST /api/analytics/journey, which validates against a whitelist
 * and hands off to the batching writer in backend/services/journey_events.py.
 *
 * Two rules this module exists to enforce:
 *
 *   1. Tracking NEVER blocks or breaks the page. Every call is fire-and-forget
 *      with a swallowed catch. If the analytics endpoint is down, slow, or
 *      returns garbage, the customer must not see a console error, a spinner,
 *      or a delayed render.
 *
 *   2. Dwell time uses sendBeacon, not fetch. A plain fetch fired during
 *      pagehide/visibilitychange is routinely cancelled when the document goes
 *      away, which would systematically lose exactly the sessions we most want
 *      to measure — the ones where someone read the report and then left.
 */

import API_BASE from '../config';

const ENDPOINT = `${API_BASE}/api/analytics/journey`;

/** The evaluation id ties every downstream event back to one free evaluation. */
export const setTrackedEvaluationId = (id) => {
  try {
    if (id) sessionStorage.setItem('evaluationId', String(id));
  } catch (_) { /* private browsing / storage disabled */ }
};

export const getTrackedEvaluationId = () => {
  try {
    return sessionStorage.getItem('evaluationId') || null;
  } catch (_) {
    return null;
  }
};

const getTrackedPhone = () => {
  try {
    const raw = sessionStorage.getItem('userInput');
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    return parsed?.whatsapp || parsed?.whatsappNumber || parsed?.phone || null;
  } catch (_) {
    return null;
  }
};

const buildPayload = (eventType, extra = {}) => ({
  event_type: eventType,
  evaluation_id: getTrackedEvaluationId(),
  phone: getTrackedPhone(),
  ...extra,
});

/**
 * Record a funnel event. Returns immediately; the request is not awaited.
 */
export const trackEvent = (eventType, extra = {}) => {
  try {
    fetch(ENDPOINT, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(buildPayload(eventType, extra)),
      keepalive: true,
    }).catch(() => {});
  } catch (_) { /* never let tracking surface an error */ }
};

/**
 * Record a funnel event during page teardown.
 *
 * sendBeacon is queued by the browser and delivered independently of the
 * document's lifetime, so it survives navigation and tab close. Falls back to
 * a keepalive fetch where sendBeacon is unavailable.
 */
export const trackEventBeacon = (eventType, extra = {}) => {
  const payload = JSON.stringify(buildPayload(eventType, extra));
  try {
    if (navigator.sendBeacon) {
      // text/plain, NOT application/json — and that is load-bearing.
      //
      // The site and the API are on different origins, so a beacon with an
      // application/json content type is not CORS-safelisted and needs a
      // preflight. sendBeacon cannot preflight, so the browser drops the
      // request without a word: dwell tracking would appear to work and
      // silently record nothing. text/plain is safelisted and goes straight
      // through; the endpoint parses the raw body itself for exactly this
      // reason.
      navigator.sendBeacon(ENDPOINT, new Blob([payload], { type: 'text/plain' }));
      return;
    }
  } catch (_) { /* fall through */ }

  try {
    fetch(ENDPOINT, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: payload,
      keepalive: true,
    }).catch(() => {});
  } catch (_) { /* give up silently */ }
};

/**
 * Measure how long the user stayed on a page and report it once.
 *
 * Call from a useEffect and return the cleanup function:
 *
 *     useEffect(() => trackDwell('report_dwell'), []);
 *
 * Fires on the first of: tab hidden, pagehide, or unmount. Guarded so that a
 * hide-then-show-then-unmount sequence reports a single event rather than
 * inflating the sample with duplicates.
 */
export const trackDwell = (eventType, extra = {}) => {
  const startedAt = Date.now();
  let sent = false;

  const send = () => {
    if (sent) return;
    sent = true;
    trackEventBeacon(eventType, { ...extra, duration_ms: Date.now() - startedAt });
  };

  const onVisibility = () => {
    if (document.visibilityState === 'hidden') send();
  };

  document.addEventListener('visibilitychange', onVisibility);
  window.addEventListener('pagehide', send);

  return () => {
    document.removeEventListener('visibilitychange', onVisibility);
    window.removeEventListener('pagehide', send);
    send();
  };
};
